"use client"
import type { ActivityFeedTableView, Persona } from "@opensea/navigation"
import {
  ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
  COLLECTION_ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
  getDefaultActivityFeedTableView,
  PROFILE_ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
  usePersona,
} from "@opensea/navigation"
import { useCookie, useOnChange } from "@opensea/ui-kit/hooks"
import { createContext, useContext } from "react"
import { LONG_LASTING_COOKIE_EXPIRATION } from "@/lib/cookie"

type ActivityFeedPage = "activity" | "collection" | "profile" | "tokenActivity"

type Size = "md" | "lg"

const PAGE_TO_COOKIE = {
  activity: ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
  tokenActivity: ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
  collection: COLLECTION_ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
  profile: PROFILE_ACTIVITY_FEED_TABLE_VIEW_COOKIE_NAME,
}

const ActivityFeedTableViewContext = createContext<{
  tableView: ActivityFeedTableView
  setTableView: (tableView: ActivityFeedTableView) => void
  size: Size
}>({
  tableView: "table",
  setTableView: () => undefined,
  size: "md",
})

type Props = {
  children: React.ReactNode
  tableView: ActivityFeedTableView
  persona: Persona
  page?: ActivityFeedPage
}

export function ActivityFeedTableViewProvider({
  children,
  tableView: initialTableView,
  page = "activity",
}: Props) {
  const { persona } = usePersona()
  const tableViewCookie = PAGE_TO_COOKIE[page]
  const [tableView, setTableViewCookie] = useCookie(tableViewCookie)
  const resolvedTableView =
    (tableView as ActivityFeedTableView | null) ?? initialTableView

  const size: Size = resolvedTableView === "table" ? "lg" : "md"

  function setTableView(next: ActivityFeedTableView) {
    setTableViewCookie(next, { expires: LONG_LASTING_COOKIE_EXPIRATION })
  }

  useOnChange(persona, () => {
    setTableView(getDefaultActivityFeedTableView(persona))
  })

  const value = {
    tableView: resolvedTableView,
    setTableView,
    size,
  }

  return (
    <ActivityFeedTableViewContext.Provider value={value}>
      {children}
    </ActivityFeedTableViewContext.Provider>
  )
}

export const useActivityFeedTableView = () =>
  useContext(ActivityFeedTableViewContext)
